import { CATALOG_OWNER_TYPES, type CatalogOwnerType } from '../models/document-owner'

/**
 * LO HEREDADO — lo que el cliente de este nivel va a ver y que no se subió acá.
 *
 * ## Por qué se muestra, si el listado propio no lo trae
 *
 * El cartapacio de un dueño lista **lo que ese dueño posee**, y eso está bien: mezclar los
 * dos conjuntos en una sola lista invitaría a «borrar» desde un proyecto un plano que vive en
 * la sociedad. Pero la consecuencia no intencionada era que el curador de un inmueble **no
 * tenía forma de ver lo que su cliente sí ve**: abría la pestaña, encontraba dos documentos y
 * concluía que el expediente estaba vacío mientras el teléfono del cliente mostraba nueve.
 *
 * La respuesta no es fundir las listas: es una SEGUNDA sección, de sólo lectura, con el nivel
 * del que viene cada documento. Ver no pide permiso (ADR 0023: «Ver NO es un permiso»), y
 * actuar sobre el origen se hace yendo al origen — que es exactamente la distinción que la
 * separación de secciones enseña sin una sola línea de ayuda.
 *
 * ## Sólo lo PÚBLICO
 *
 * Un heredado interno no lo ve nadie, ni el cliente ni los niveles de abajo: listarlo acá
 * sería mostrar el escritorio de otro equipo. El resolver del servidor ya filtra por
 * `visibility: public`; este módulo no vuelve a decidirlo, sólo lo ordena y lo rotula.
 */

/**
 * Lo MÍNIMO que esta regla necesita de una fila heredada: cómo se llama y de dónde viene.
 *
 * No es el DTO recortado por comodidad. Agrupar por origen no mira el tipo de archivo, ni el
 * tamaño, ni si tiene miniatura, y declararlo así es lo que permite que las funciones de abajo
 * sean genéricas: la tarjeta recibe la fila COMPLETA del contrato, con sus miniaturas y sus
 * páginas, sin que este módulo tenga que conocer —ni volver a declarar— cada campo que el
 * contrato agregue mañana.
 */
export type InheritedDocumentRow = {
  name: string
  /** De qué nivel de la cadena viene. Nunca es el dueño que se está mirando. */
  originType: CatalogOwnerType
}

/**
 * Cómo se nombra el origen en la fila.
 *
 * En tercera persona y con el artículo, para que la frase completa se lea sola: «Del
 * proyecto», «De la sociedad». No lleva el NOMBRE de la entidad a propósito: en una cadena
 * hay a lo sumo un proyecto y una sociedad por encima, así que el nombre no desambigua nada
 * —confirma— y a cambio costaría una consulta por nivel en cada apertura de la pestaña.
 */
export const INHERITED_ORIGIN_LABEL: Record<CatalogOwnerType, string> = {
  society: 'De la sociedad',
  project: 'Del proyecto',
  'type-property': 'Del subtipo',
  property: 'Del inmueble'
}

/**
 * De más específico a más general, que es el orden en que la herencia resuelve y el orden en
 * que un curador piensa: primero lo que casi le pertenece, al final lo que viene de arriba de
 * todo. `CATALOG_OWNER_TYPES` ya declara esa secuencia; leerla de ahí evita que dos listas se
 * desincronicen el día que entre un nivel nuevo.
 */
const ORIGIN_ORDER: CatalogOwnerType[] = [...CATALOG_OWNER_TYPES].reverse()

export type InheritedGroup<TDocument extends InheritedDocumentRow = InheritedDocumentRow> = {
  originType: CatalogOwnerType
  label: string
  documents: TDocument[]
}

/**
 * Agrupa lo heredado por NIVEL de origen, no por categoría.
 *
 * Es deliberado y es la diferencia con la sección de arriba: en lo propio el curador
 * clasifica, y ahí la categoría es la estructura útil. En lo heredado no puede tocar nada —lo
 * único que necesita saber es **a quién ir a pedírselo**, y eso es el nivel.
 */
export function groupInheritedByOrigin<TDocument extends InheritedDocumentRow>(
  documents: TDocument[]
): InheritedGroup<TDocument>[] {
  return ORIGIN_ORDER.flatMap((originType) => {
    const rows = documents.filter((document) => document.originType === originType)
    if (rows.length === 0) return []
    return [
      {
        originType,
        label: INHERITED_ORIGIN_LABEL[originType],
        documents: [...rows].sort((left, right) =>
          left.name.localeCompare(right.name, 'es', { numeric: true, sensitivity: 'base' })
        )
      }
    ]
  })
}

/**
 * Qué niveles puede heredar un dueño. Una sociedad no hereda de nadie.
 *
 * La UI lo consulta para no pedir una lista que por construcción va a venir vacía, y —más
 * importante— para no pintarle a quien cura una sociedad una sección de heredados que nunca
 * va a tener contenido: un vacío permanente se lee como un error de carga.
 */
export function inheritsFromAnyLevel(ownerType: CatalogOwnerType): boolean {
  return ownerType !== 'society'
}
